import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Package, ShoppingCart, DollarSign, Users, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI, productsAPI } from '../api';

const AdminDashboard = () => {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [syncingStock, setSyncingStock] = useState(false);
  const [syncingGallery, setSyncingGallery] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: '',
    price: '',
    description: '',
    specifications: '',
    compliance: '',
    image: ''
  });

  useEffect(() => {
    if (!isAdmin()) return;
    const load = async () => {
      try {
        const [p, o] = await Promise.all([
          productsAPI.getAll(),
          adminAPI.getAllOrders().catch(() => [])
        ]);
        setProducts(p || []);
        setOrders(o || []);
      } catch (e) {
        console.error('Failed to load admin data', e);
        toast.error('Failed to load products/orders');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin]);

  if (!isAdmin()) {
    return <Navigate to="/" />;
  }

  const stats = {
    totalProducts: products.length,
    totalOrders: orders.length,
    totalRevenue: orders.reduce((sum, order) => sum + (order.total || order.total_amount || 0), 0),
    pendingOrders: orders.filter(o => (o.status || '').toLowerCase() === 'processing' || (o.status || '').toLowerCase() === 'pending').length
  };

  const handleAddProduct = (e) => {
    e.preventDefault();
    const product = {
      id: products.length + 1,
      ...newProduct,
      price: parseFloat(newProduct.price),
      inStock: true,
      featured: false
    };
    setProducts([...products, product]);
    toast.success('Product added successfully!');
    setShowAddProduct(false);
    setNewProduct({
      name: '',
      category: '',
      price: '',
      description: '',
      specifications: '',
      compliance: '',
      image: ''
    });
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await adminAPI.updateOrderStatus(orderId, newStatus);
      setOrders(orders.map(order =>
        (order.id === orderId || order._id === orderId) ? { ...order, status: newStatus } : order
      ));
      toast.success('Order status updated!');
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message || 'Failed to update status';
      toast.error(`Update failed: ${detail}`);
    }
  };

  const refreshProducts = async () => {
    try {
      const p = await productsAPI.getAll();
      setProducts(p || []);
    } catch (e) {
      console.error('Failed to refresh products', e);
    }
  };

  const handleSyncStock = async () => {
    if (syncingStock) return;
    setSyncingStock(true);
    try {
      const result = await adminAPI.syncProductStock();
      toast.success(result.message || 'Stock synced successfully!');
      await refreshProducts();
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message || 'Failed to sync stock';
      toast.error(`Sync failed: ${detail}`);
    } finally {
      setSyncingStock(false);
    }
  };

  const handleSyncGallery = async () => {
    if (syncingGallery) return;
    setSyncingGallery(true);
    try {
      const result = await adminAPI.syncProductGallery();
      toast.success(result.message || 'Product galleries synced!');
      await refreshProducts();
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message || 'Failed to sync gallery';
      toast.error(`Gallery sync failed: ${detail}`);
    } finally {
      setSyncingGallery(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <div className="flex flex-wrap gap-2 self-start sm:self-auto">
            <Button
              onClick={handleSyncStock}
              disabled={syncingStock}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="sync-stock-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncingStock ? 'animate-spin' : ''}`} />
              {syncingStock ? 'Syncing Stock…' : 'Sync Stock'}
            </Button>
            <Button
              onClick={handleSyncGallery}
              disabled={syncingGallery}
              variant="outline"
              className="border-orange-600 text-orange-700 hover:bg-orange-50"
              data-testid="sync-gallery-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncingGallery ? 'animate-spin' : ''}`} />
              {syncingGallery ? 'Syncing Gallery…' : 'Sync Product Gallery'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Products</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalProducts}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalOrders}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    KES {stats.totalRevenue.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Orders</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.pendingOrders}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>All Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orders.length === 0 && !loading && (
                    <p className="text-gray-500">No orders yet.</p>
                  )}
                  {orders.map(order => {
                    const orderId = order.id || order._id || '';
                    const total = order.total ?? order.total_amount ?? 0;
                    const items = order.items || [];
                    const customer = order.customerName || order.customer_name || order.name || '';
                    const email = order.email || order.customer_email || '';
                    const phone = order.phone || order.customer_phone || '';
                    const dateStr = order.date || order.created_at || '';
                    const deliveryAddress = order.deliveryAddress || order.shipping_address || '';
                    const paymentMethod = order.paymentMethod || order.payment_method || '';
                    return (
                      <div key={orderId} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-gray-900">Order #{String(orderId).slice(-8)}</p>
                            {customer && <p className="text-sm text-gray-600">{customer}</p>}
                            {email && <p className="text-sm text-gray-600">{email}</p>}
                            {phone && <p className="text-sm text-gray-600">{phone}</p>}
                            {dateStr && <p className="text-sm text-gray-600 mt-1">{new Date(dateStr).toLocaleString()}</p>}
                          </div>
                          <div className="text-right">
                            <Badge className="mb-2">{order.status || 'pending'}</Badge>
                            <p className="text-lg font-bold">KES {Number(total).toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="space-y-2 mb-3">
                          {items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-gray-600">
                                {(item.productName || item.product_name || item.name)} x {item.quantity}
                              </span>
                              <span>KES {Number((item.price || 0) * (item.quantity || 0)).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>

                        <div className="text-sm text-gray-600 mb-3">
                          {deliveryAddress && <p><strong>Delivery:</strong> {typeof deliveryAddress === 'string' ? deliveryAddress : JSON.stringify(deliveryAddress)}</p>}
                          {paymentMethod && <p><strong>Payment:</strong> {paymentMethod}</p>}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateOrderStatus(orderId, 'Processing')}
                          >
                            Processing
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateOrderStatus(orderId, 'Shipped')}
                          >
                            Shipped
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateOrderStatus(orderId, 'Delivered')}
                          >
                            Delivered
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>All Products</CardTitle>
                <Button
                  onClick={() => setShowAddProduct(!showAddProduct)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </CardHeader>
              <CardContent>
                {showAddProduct && (
                  <form onSubmit={handleAddProduct} className="border rounded-lg p-4 mb-6 space-y-4">
                    <h3 className="font-semibold text-lg">Add New Product</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Product Name</Label>
                        <Input
                          id="name"
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="category">Category</Label>
                        <Input
                          id="category"
                          value={newProduct.category}
                          onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="price">Price (KES)</Label>
                        <Input
                          id="price"
                          type="number"
                          value={newProduct.price}
                          onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="image">Image URL</Label>
                        <Input
                          id="image"
                          value={newProduct.image}
                          onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })}
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={newProduct.description}
                          onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="specifications">Specifications</Label>
                        <Textarea
                          id="specifications"
                          value={newProduct.specifications}
                          onChange={(e) => setNewProduct({ ...newProduct, specifications: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="compliance">Compliance Standards</Label>
                        <Input
                          id="compliance"
                          value={newProduct.compliance}
                          onChange={(e) => setNewProduct({ ...newProduct, compliance: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                        Add Product
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowAddProduct(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {loading && (
                    <p className="text-gray-500 col-span-full">Loading products…</p>
                  )}
                  {!loading && products.length === 0 && (
                    <p className="text-gray-500 col-span-full">No products found.</p>
                  )}
                  {products.map(product => {
                    const key = product.id || product._id || product.name;
                    const primaryImage = (Array.isArray(product.images) && product.images.length > 0)
                      ? product.images[0]
                      : product.image;
                    const stock = product.stock ?? 0;
                    return (
                      <div key={key} className="border rounded-lg p-4">
                        <img
                          src={primaryImage}
                          alt={product.name}
                          className="w-full h-40 object-cover rounded-lg mb-3 bg-gray-50"
                        />
                        <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{product.category}</p>
                        <div className="flex items-center justify-between">
                          <p className="text-lg font-bold text-gray-900">KES {Number(product.price || 0).toLocaleString()}</p>
                          <Badge variant={stock > 0 ? 'default' : 'destructive'}>
                            {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
